import {useState, useEffect} from 'react';

import {checkUniquenessByKey} from 'components/table/utils/table.utils';
import {IFieldMeta} from 'entities/metadata';
import {selectEntityData, TEntity} from 'modules/data';
import {ITableEntityData} from 'modules/data/data-types';
import {EEntityType} from 'shared/constants/entities';
import {EFormFieldWatcherActions, EFormFieldWatcherConditions, EFormFieldWatcherConnective} from 'shared/constants/watchers';
import {useEntityFromLocation} from 'shared/hooks/use-entity-from-location';
import {IFormFieldWatcher} from 'shared/types/watchers';
import {createConnectiveJoiner} from 'shared/utils/watchers';
import {useAppSelector} from 'store/config/hooks';

// Функция проверки отдельного условия
const checkCondition = (conditionConfig: any, entity: TEntity, rows: TEntity[] = []) => {
    const {target, when, withValue} = conditionConfig;
    const targetValue = target ? entity?.[target] : undefined;

    // Нормализация значений для сравнения
    // В таблицах значения часто могут быть null, undefined или строками
    const isTargetSet = targetValue !== null && targetValue !== undefined && targetValue !== '';

    switch (when) {
        case EFormFieldWatcherConditions.set:
            return isTargetSet;

        case EFormFieldWatcherConditions.unset:
            return !isTargetSet;

        case EFormFieldWatcherConditions.equals:
        case EFormFieldWatcherConditions.equalsToValue:
            // Простое сравнение, можно расширить для сложных типов если нужно
            return String(targetValue) === String(withValue);

        case EFormFieldWatcherConditions.notEqualsToValue:
            return String(targetValue) !== String(withValue);

        case EFormFieldWatcherConditions.unique:
            return checkUniquenessByKey(rows, target);

         // Добавить другие условия по мере необходимости (например, fieldEqualsToValue)
        default:
            return false;
    }
};

export const checkWatchersDisabled = (
    watchers: IFormFieldWatcher[] | undefined,
    record: TEntity,
    rows: TEntity[] = []
): boolean => {
    if (!watchers?.length) {
        return false;
    }

    let isDisabled = false;

    watchers.forEach((watcher: any) => {
        const {conditions, perform} = watcher;

        // Если действие не связано с disable, пропускаем (расширить при необходимости)
        const actions = Array.isArray(perform) ? perform : [perform];
        const hasDisableAction = actions.includes(EFormFieldWatcherActions.disable);
        const hasEnableAction = actions.includes(EFormFieldWatcherActions.enableField);

        if (!hasDisableAction && !hasEnableAction) return;

        let isWatcherTriggered: boolean | null = null;

        conditions.forEach((conditionConfig: any) => {
            const isTriggered = checkCondition(conditionConfig, record, rows);

            const connective = conditionConfig.withConnective || EFormFieldWatcherConnective.AND;
            const joinCondition = createConnectiveJoiner(connective);

            if (isWatcherTriggered === null) {
                isWatcherTriggered = connective === EFormFieldWatcherConnective.NOT
                    ? !isTriggered
                    : isTriggered;
            } else {
                isWatcherTriggered = joinCondition(isWatcherTriggered, isTriggered);
            }
        });

        if (isWatcherTriggered) {
            if (hasDisableAction) isDisabled = true;
            if (hasEnableAction) isDisabled = false;
        } else {
            if (hasDisableAction) isDisabled = false;
            if (hasEnableAction) isDisabled = true;
        }
    });

    return isDisabled;
};

interface IUseTableFieldWatcherArgs {
    fieldMeta: IFieldMeta;
    record: TEntity;
}

/**
 * Хук для управления динамическим состоянием ячейки таблицы на основе конфигурации `watchers`.
 * Представляет собой облегченную версию системного `useFormWatcher`, оптимизированную
 * для работы внутри табличных структур без использования контекста формы Ant Design.
 * ### Функциональность:
 * - Вычисляет состояние `isDisabled` на основе данных текущей строки (`record`).
 * - Поддерживает цепочки условий с логическими операторами (AND, OR, NOT).
 * - Обрабатывает атомарные условия: `set`, `unset`, `equals`, `notEquals`.
 * @param {IUseTableFieldWatcherArgs} args - Объект аргументов хука.
 * @param {IFieldMeta} args.fieldMeta - Метаданные поля, содержащие массив конфигураций `watchers`.
 * @param {TEntity} args.record - Объект данных текущей строки таблицы.
 * @returns {Object} Объект с состоянием ячейки.
 * @returns {boolean} .isDisabled - Флаг, указывающий, должна ли ячейка быть заблокирована для редактирования.
 */
export const useTableCellWatcher = ({fieldMeta, record}: IUseTableFieldWatcherArgs) => {
    const [isDisabled, setIsDisabled] = useState(false);

    const {entityName: entityNameFromLocation} = useEntityFromLocation();
    const tableData = (useAppSelector(selectEntityData(entityNameFromLocation, EEntityType.TABLE))) as ITableEntityData;

    const {rows = []} = tableData || {};

    useEffect(() => {
        const watchersDisabled = checkWatchersDisabled(fieldMeta?.watchers, record, rows);
        setIsDisabled(watchersDisabled);
    }, [record, fieldMeta, rows]);

    return {
        isDisabled
    };
};